import { getLocaleString } from '@assets/i18n/config';
import type { WatchlistAccount, WatchlistTrigger } from '@interfaces/watchList';
import { Chip } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import type { ColumnDef } from '@ovComponents/2-component/dataTable';
import { Tooltip } from '@ovComponents/2-component/tooltip/tooltip';
import { AssetProgressBar } from '@ovComponents/3-pattern/assetProgressBar/assetProgressBar';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { WATCH_LIST_COPY } from './watchListCopy';

/**
 * Returns display props for the reason chip.
 * Kept as a helper so the label logic is testable in isolation.
 */
export function getReasonChipProps(trigger: WatchlistTrigger): {
  label: string;
  tooltip?: string;
} {
  if (!trigger.occurredAt) {
    return { label: trigger.reason };
  }

  const tooltip = new Intl.DateTimeFormat(getLocaleString(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(trigger.occurredAt));

  return { label: trigger.reason, tooltip };
}

/**
 * Column definitions for the Watch List DataTable.
 *
 * Rendered as a hook so column cells can read theme tokens.
 *
 * Notes:
 * - The reason cell uses a plain MUI `<Chip variant='outlined'>` rather than `AlertChip`
 *   because `AlertChip` only accepts 'checkmark' | 'problem' | 'cancel' icons — none of
 *   which are neutral/informational. A follow-up PR should extend `AlertChip` with an
 *   `icon='info' | 'none'` option, at which point this cell can switch to `<AlertChip>`.
 * - `bandConsumedPercentage` = max(cash, position) — the worst-of dimension. This is
 *   the sort key; the bar renders both dimensions side-by-side.
 */
export function useWatchListColumns(): ColumnDef<WatchlistAccount, unknown>[] {
  const { sys } = useThemeTokens();

  const columns: ColumnDef<WatchlistAccount, unknown>[] = [
    {
      id: 'portfolio',
      accessorFn: (row) => row.portfolioName,
      header: WATCH_LIST_COPY.table.portfolio,
      enableSorting: true,
      cell: ({ row }) => (
        <Box>
          <Typography variant='bodySmall' weight='bold'>
            {row.original.portfolioName}
          </Typography>
          <Typography variant='labelSmall' colorVariant='variant'>
            {row.original.accountNumber} · {row.original.householdName}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'accountType',
      accessorKey: 'accountType',
      header: WATCH_LIST_COPY.table.accountType,
      enableSorting: false,
      cell: ({ getValue }) => (
        <Box
          sx={{
            display: 'inline-flex',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: sys.color.primaryVariant,
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{ color: sys.color.onPrimaryVariant }}
          >
            {getValue<string>()}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'reason',
      accessorFn: (row) => row.trigger.reason,
      header: WATCH_LIST_COPY.table.reason,
      enableSorting: false,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const { label, tooltip } = getReasonChipProps(row.original.trigger);
        // VIEW-ONLY: neutral chip — no problem/cancel severity icon.
        // AlertChip only accepts 'checkmark' | 'problem' | 'cancel'; none are neutral.
        // Using MUI Chip variant='outlined' as a fallback until AlertChip gains 'info'/'none'.
        const chip = (
          <Chip
            variant='outlined'
            size='small'
            label={label}
            sx={{ fontWeight: 600, fontSize: 12 }}
          />
        );

        return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
      },
    },
    {
      id: 'custodian',
      accessorKey: 'custodian',
      header: WATCH_LIST_COPY.table.custodian,
      enableSorting: true,
    },
    {
      id: 'portfolioValue',
      accessorKey: 'portfolioValue',
      header: WATCH_LIST_COPY.table.value,
      enableSorting: true,
      meta: { dataTable: { align: 'right', numeric: true } },
      cell: ({ getValue }) =>
        new Intl.NumberFormat(getLocaleString(), {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(getValue<number>()),
    },
    {
      id: 'bandConsumed',
      accessorKey: 'bandConsumedPercentage',
      header: WATCH_LIST_COPY.table.bandConsumed,
      enableSorting: true,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}
        >
          <AssetProgressBar
            data={[
              {
                key: 'cash',
                percentage: row.original.bandConsumedCashPercentage,
                colorCode: sys.color.warning ?? '#ed6c02',
              },
              {
                key: 'position',
                percentage: row.original.bandConsumedPositionPercentage,
                colorCode: sys.color.warning ?? '#ed6c02',
              },
            ]}
            sx={{ flex: 1 }}
          />
          <Typography
            variant='labelSmall'
            sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            {`${Math.round(row.original.bandConsumedPercentage)}%`}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'threshold',
      accessorKey: 'thresholdLabel',
      header: WATCH_LIST_COPY.table.threshold,
      enableSorting: false,
      cell: ({ getValue }) => (
        <Typography variant='bodySmall'>{getValue<string>()}</Typography>
      ),
    },
  ];

  return columns;
}
